import * as React from 'react';

import {Button} from 'shells/button/button';
import {getUserFacingNoun} from 'shells/edition/get-user-facing-noun';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from 'shells/icon/svg/upgrade-arrow-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {ThumbnailButtonCard} from 'shells/thumbnail/thumbnail-button-card/thumbnail-button-card';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {AbTestingFeatureGate} from '../../../limits/feature-gates/ab-testing-feature-gate-modal';

import {appHistory} from '../../../../history';

import {WinnerBadge} from './winner-badge';

import styles from './edition-content/a-b-test-details.module.css';

type Props = {
    edition: Edition;
    isABTesting: boolean;
    setShouldShowConfirmation: (arg1: boolean) => void;
    comparisonSubject: string | null | undefined;
    comparisonPreviewText: string | null | undefined;
    comparisonThumbnailUrl: string | null | undefined;
};

export function ContentPreview({setShouldShowConfirmation, edition, ...props}: Props) {
    const containerRef = React.useRef(null);
    const [thumbnailHeight, setThumbnailHeight] = React.useState(0);
    const [showFeatureGate, setShowFeatureGate] = React.useState(false);
    const [, height] = useResizeObserver(containerRef.current);

    React.useEffect(() => {
        if (containerRef.current) {
            // @ts-expect-error
            setThumbnailHeight(containerRef.current.clientHeight);
        }
        if (typeof height === 'number') {
            setThumbnailHeight(height);
        }
    }, [height]);

    const EmptyState = () => (
        <div className={styles['empty-state-container']}>
            <EmptyStateLarge
                title='Refine your message with A/B testing'
                icon={<Illustration src='a-b-test' width={173} />}
                cta={
                    <StackLegacy spacing={12} alignCenter={true}>
                        <div className='mb-16 text-align-center font-size-normal'>
                            See what resonates best with your audience. A/B test this{' '}
                            {getUserFacingNoun(edition.campaign.type)} by comparing two message
                            versions. The winner will automatically be sent to remaining recipients.
                        </div>
                        {NutClientConfig.hasAbTesting ? (
                            <Button
                                variant='primary'
                                size='xlarge'
                                onClick={() => setShouldShowConfirmation(true)}
                            >
                                Enable A/B test
                            </Button>
                        ) : (
                            <Button
                                variant='upgrade'
                                size='xlarge'
                                onClick={() => setShowFeatureGate(true)}
                            >
                                <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                                    <UpgradeArrowIcon size={16} />
                                    Upgrade to enable A/B testing
                                </StackLegacy>
                            </Button>
                        )}
                        <Link
                            href='https://support.nutshell.com/en/articles/8717492-campaigns-how-to-set-up-a-b-testing'
                            variant='secondary'
                            newTab={true}
                        >
                            Learn more
                        </Link>
                    </StackLegacy>
                }
            />
        </div>
    );

    const winningVersionIsA =
        edition.comparison &&
        edition.comparison.winnerEdition &&
        edition.comparison.winnerEdition.id === edition.id;
    const winningVersionIsB =
        edition.comparison &&
        edition.comparison.winnerEdition &&
        edition.comparison.winnerEdition.id !== edition.id;

    const handleSelectVersion = (version: 'A' | 'B') => {
        const strippedSearch = window.location.search.replace(/&version=[AB]/, '');
        appHistory.push({
            pathname: `${(edition && edition.htmlUrlPath) || ''}/editor`,
            search: `${strippedSearch}&version=${version}`,
        });
    };

    return props.isABTesting ? (
        <div style={{height: '100%'}} className='flex flex-dir-col'>
            <div style={{height: '50%'}} ref={containerRef} className='mb-16'>
                <ThumbnailButtonCard
                    key={edition.id}
                    title={
                        edition.emailTemplate && winningVersionIsA ? (
                            <StackLegacy spacing={12}>
                                <WinnerBadge isWinner={true} removeMargin={true} />
                                <strong className={styles.title}>
                                    {edition.emailTemplate.subject || 'No subject'}
                                </strong>
                            </StackLegacy>
                        ) : (
                            (edition.emailTemplate && edition.emailTemplate.subject) || 'No subject'
                        )
                    }
                    secondaryInfo={
                        edition.emailTemplate && edition.emailTemplate.previewText
                            ? edition.emailTemplate.previewText
                            : ''
                    }
                    tertiaryInfo='Version A'
                    thumbnail={edition.emailTemplate && edition.emailTemplate.thumbnailUrl}
                    thumbnailHeight={
                        winningVersionIsA ? thumbnailHeight * 0.65 : thumbnailHeight * 0.7
                    }
                    onClick={() => handleSelectVersion('A')}
                    backgroundColor={edition.backgroundColor}
                />
            </div>
            <div style={{height: '50%'}}>
                <ThumbnailButtonCard
                    key={2}
                    title={
                        edition.emailTemplate && winningVersionIsB ? (
                            <StackLegacy spacing={12}>
                                <WinnerBadge isWinner={true} removeMargin={true} />
                                <strong className={styles.title}>
                                    {props.comparisonSubject || 'No subject'}
                                </strong>
                            </StackLegacy>
                        ) : (
                            props.comparisonSubject || 'No subject'
                        )
                    }
                    secondaryInfo={props.comparisonPreviewText}
                    tertiaryInfo='Version B'
                    thumbnail={props.comparisonThumbnailUrl}
                    thumbnailHeight={
                        winningVersionIsB ? thumbnailHeight * 0.65 : thumbnailHeight * 0.7
                    }
                    onClick={() => handleSelectVersion('B')}
                    backgroundColor={edition.backgroundColor}
                />
            </div>
        </div>
    ) : (
        <>
            <EmptyState />
            {showFeatureGate && (
                <AbTestingFeatureGate isOpen={showFeatureGate} setIsOpen={setShowFeatureGate} />
            )}
        </>
    );
}
